import _ from 'lodash'
import {
  MessagePriority,
  MessageRecipientStatus,
  MessageSubType,
  MessageType,
  PatientCareCardActionType,
  PatientCareCardType,
  PatientCareCardUIType,
} from './enums'

export default function(store, { host }) {
  const Message = store.defineModel('message', {
    replaceKeys: {
      clientId: 'id',
      createdTime: 'createdAt',
      data: 'metadata',
      dndAutoForwardedFrom: 'autoForwardedFromId',
      dor: 'deleteOnRead',
      escalationExecId: 'escalationExecutionId',
      groupAvatar: 'groupAvatarUrl',
      groupToken: 'groupId',
      messageId: 'id',
      originalData: 'originalMetadata',
      originalMsgId: 'originalMessageId',
      originalSenderName: 'originalSenderDisplayName',
      originalTimestamp: 'originalForwardedMessageCreatedAt',
      recipient: 'recipientId',
      recipientOrganization: 'recipientOrganizationId',
      recipientOrgToken: 'recipientOrganizationId',
      recipientToken: 'recipientId',
      sender: 'senderId',
      senderAvatar: 'senderAvatarUrl',
      senderOrganization: 'senderOrganizationId',
      senderOrgToken: 'senderOrganizationId',
      senderToken: 'senderId',
    },

    transientAttrs: [
      '__organizationId',
      '$synced',
      'escalationExecutionChange',
      'groupMembersChange',
      'inTimeline',
    ],

    defaultValues: {
      inTimeline: false,
      isUnread: true,
      messageType: MessageType.USER_SENT,
      senderStatus: MessageRecipientStatus.NEW,
      shouldEnsureRecipientStatus: false,
      sortNumber: 0,
    },

    skipCamelizationForKeys: [
      'escalation',
      'metadata',
      'originalMetadata',
    ],

    parseAttrs(attrs) {
      // server returns "attachment_name" field on the message itself and "attachments" as an array
      // since currently only a single attachment is allowed, need to merge the two
      // server may return "attachment_name":"undefined" as a string, so need to filter that
      if ('attachmentName' in attrs) {
        let attachmentName = attrs.attachmentName
        delete attrs.attachmentName
        if (
          attachmentName !== 'undefined' && Array.isArray(attrs.attachments) &&
          attrs.attachments.length > 0 && attachmentName
        ) {
          attrs.attachments[0].name = attachmentName
        }
      }

      if (Array.isArray(attrs.attachments)) {
        attrs.attachments.forEach((attachment, i) => (attachment.id = `${i + 1}`))
      }

      if ('priority' in attrs) {
        attrs.priority = MessagePriority.resolve(attrs.priority)
      }

      if (('createdAt' in attrs) && typeof attrs.createdAt === 'string') {
        attrs.createdAt = new Date(attrs.createdAt)
      }

      if ('expireIn' in attrs) {
        attrs.expiresAt = Date.now() + attrs.expireIn * 1000
        delete attrs.expireIn
      }

      if ('recipientStatus' in attrs) {
        delete attrs.recipientStatus
      }
      if ('status' in attrs) {
        delete attrs.status
      }
      if ('xmlns' in attrs) {
        delete attrs.xmlns
      }

      if ('escalation' in attrs) {
        const execution = host.escalations._updateEscalation(attrs.escalation)
        delete attrs.escalation
        attrs.escalationExecutionId = execution.id
      }

      if ('subType' in attrs) {
        attrs.subType = MessageSubType.resolve(attrs.subType)
      }

      // convert metadata JSON string payloads into objects
      if (attrs.metadata) {
        attrs.metadata = _.castArray(attrs.metadata).map(host.metadata.__parseMessageMetadata)
          .filter(Boolean)

        const senderRoleAttrs = attrs.metadata.find(({ namespace }) => (
          namespace === 'tigertext:message:role:sender'
        ))

        const roleOwnerAttrs = attrs.metadata.find(({ namespace }) => (
          namespace === 'tigertext:message:role:owner'
        ))

        const patientCareAttrs = attrs.metadata.find(({ namespace }) => (
          namespace === 'tigertext:message:patient_care'
        ))

        if (senderRoleAttrs && senderRoleAttrs.payload) {
          const { senderOrganizationId: organizationId } = attrs
          const metadata = senderRoleAttrs.payload
          attrs.senderRole = host.roles.__parseRoleFromMetadata({ metadata, organizationId })
          attrs.currentSenderRoleId = attrs.senderRole.id
        }

        if (roleOwnerAttrs && roleOwnerAttrs.payload) {
          const { senderOrganizationId: organizationId, senderId } = attrs
          const metadata = roleOwnerAttrs.payload
          attrs.senderId = metadata.token

          if (senderId !== metadata.token) {
            attrs.senderRole = {
              $entityType: 'role',
              botUserId: senderId,
              id: `role:${senderId}`,
              organizationId,
            }
            attrs.currentSenderRoleId = `role:${senderId}`
          }
        }

        if (patientCareAttrs && patientCareAttrs.payload) {
          const patientCareCardItems = []

          for (const item of patientCareAttrs.payload.components) {
            if ('type' in item) {
              item.type = PatientCareCardUIType.resolve(item.type)
            }
            if ('sub_type' in item) {
              item.sub_type = PatientCareCardType.resolve(item.sub_type)
            }
            if ('action_type' in item) {
              item.action_type = PatientCareCardActionType.resolve(item.action_type)
            }
            patientCareCardItems.push(item)
          }
          attrs.patientCareCard = patientCareCardItems
        }
      } else if ('metadata' in attrs) {
        delete attrs['metadata']
      }

      if (attrs.originalMetadata) {
        attrs.originalMetadata = _.castArray(attrs.originalMetadata).map(host.metadata.__parseMessageMetadata)
          .filter(Boolean)

        const senderRoleAttrs = attrs.originalMetadata.find(({ namespace }) => (
          namespace === 'tigertext:message:role:sender'
        ))

        const roleOwnerAttrs = attrs.originalMetadata.find(({ namespace }) => (
          namespace === 'tigertext:message:role:owner'
        ))

        if (senderRoleAttrs && senderRoleAttrs.payload) {
          const { senderOrganizationId: organizationId } = attrs
          const metadata = senderRoleAttrs.payload
          attrs.originalSenderRole = host.roles.__parseRoleFromMetadata({ metadata, organizationId })
        }

        if (roleOwnerAttrs && roleOwnerAttrs.payload) {
          const {
            originalSender,
            originalSenderDisplayName,
            originalSenderRole,
            senderOrganizationId: organizationId,
          } = attrs
          const metadata = roleOwnerAttrs.payload

          if (!originalSenderRole && typeof originalSender === 'string') {
            attrs.originalSenderRole = {
              $entityType: 'role',
              botUserId: originalSender,
              displayName: originalSenderDisplayName,
              id: `role:${originalSender}`,
              organizationId,
            }
          }

          if (!originalSender || (originalSender && typeof originalSender === 'string')) {
            attrs.originalSender = {
              $entityType: 'user',
              displayName: metadata.display_name,
              id: metadata.token,
              organizationId,
            }
          }
        }
      }

      if ('originalSender' in attrs && typeof attrs.originalSender === 'string') {
        const { senderOrganizationId: organizationId, originalSender, originalSenderDisplayName } = attrs

        attrs.originalSender = {
          $entityType: 'user',
          displayName: originalSenderDisplayName,
          id: originalSender,
          organizationId,
        }
        delete attrs.originalSenderDisplayName
      }

      return attrs
    },

    afterAssignment(entity) {
      let {
        __organizationId,
        counterParty,
        counterPartyId,
        counterPartyType,
        createdAt,
        isOutgoing,
        recipientOrganizationId,
        senderId,
        senderOrganizationId,
      } = entity
      const { currentUserId } = host

      if (counterParty) {
        counterPartyId = counterParty.id
        counterPartyType = counterParty.$entityType
        switch (counterPartyType) {
          case 'group':
            entity.groupId = counterPartyId
            delete entity.recipientId
            break
          case 'distributionList':
            entity.distributionListId = counterPartyId
            delete entity.recipientId
            break
        }
      } else {
        counterParty = counterPartyType && counterPartyId ? (
          store.store.get(counterPartyType, counterPartyId)
        ) : null
      }

      entity.counterParty = counterParty
      entity.counterPartyId = counterPartyId
      entity.counterPartyType = counterPartyType

      if (!createdAt) {
        createdAt = new Date()
      }

      if (senderId) {
        isOutgoing = host.messages._isMessageOutgoing(entity)
      }

      if (isOutgoing && senderOrganizationId) {
        __organizationId = senderOrganizationId
      } else if (!isOutgoing && recipientOrganizationId) {
        __organizationId = recipientOrganizationId
      }

      entity.__organizationId = __organizationId
      entity.canRecall = counterPartyType !== 'distributionList' && senderId === currentUserId
      entity.createdAt = createdAt
      entity.isOutgoing = isOutgoing
      entity.isAutoForwarded = !!entity.autoForwardedFromId

      if (!entity.subType) {
        entity.subType = MessageSubType.NA
      }

      host.messages._setRecipientStatus(entity)
    },

    relations: {
      belongsTo: {
        autoForwardedFrom: { type: 'user', foreignKey: 'autoForwardedFromId' },
        conversation: { type: 'conversation', foreignKey: 'conversationId' },
        currentSenderRole: { type: 'role', foreignKey: 'currentSenderRoleId' },
        distributionList: { type: 'distributionList', foreignKey: 'distributionListId' },
        escalationExecution: { type: 'escalationExecution', foreignKey: 'escalationExecutionId' },
        group: { type: 'group', foreignKey: 'groupId' },
        recipient: { type: 'user', foreignKey: 'recipientId' },
        recipientOrganization: { type: 'organization', foreignKey: 'recipientOrganizationId' },
        sender: { type: 'user', foreignKey: 'senderId' },
        senderOrganization: { type: 'organization', foreignKey: 'senderOrganizationId' },
      },
      hasMany: {
        statusesPerRecipient: { type: 'messageStatusPerRecipient', foreignKey: 'messageId' },
      }
    },
  })

  return Message
}
